import type {
  ActionKind,
  Attachment,
  Event,
  Permission,
  PermissionLevel,
  PermissionLimit,
  TaskTitle,
} from '@pa/core';
import type { ApprovalFrame } from './ws';

export type { ActionKind, Permission, PermissionLevel, PermissionLimit };

/**
 * API location.
 * - dev: Vite proxies `/api/*` to the Worker on :8787 (see vite.config.ts).
 * - staging: VITE_API_URL=https://api-staging.two.you (from .env.staging, `vite build --mode
 *   staging`). The SPA on staging.two.you and the API are different origins on the same site, so
 *   the SameSite=Lax session cookie is sent with `credentials: 'include'` and on the WebSocket
 *   upgrade. Prod will follow the same pattern with app.two.you → api.two.you.
 */
const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
export const DEBUG: boolean = String(import.meta.env.VITE_DEBUG ?? '') === 'true';
/** Sample data for design review only. Compile-time constant: demo modules are excluded otherwise. */
export const DEMO: boolean = String(import.meta.env.VITE_DEMO ?? '') === 'true';

export function apiUrl(path: string): string {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

/** Private media URL (session-checked by the API; never a public object URL). */
export function mediaUrl(attachmentId: string): string {
  return apiUrl(`/media/${encodeURIComponent(attachmentId)}`);
}

export function wsUrl(path: string): string {
  const http = API_BASE.startsWith('http')
    ? new URL(API_BASE)
    : new URL(API_BASE, window.location.origin);
  http.protocol = http.protocol === 'https:' ? 'wss:' : 'ws:';
  http.pathname = `${http.pathname.replace(/\/$/, '')}${path}`;
  http.search = '';
  return http.toString();
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown,
  ) {
    super(`${status} ${code}`);
  }
  /** Seconds the server asked us to wait (rate limits, cooldowns). */
  get retryAfterSeconds(): number | undefined {
    const b = this.body;
    if (b && typeof b === 'object' && 'retry_after_seconds' in b) {
      const v = (b as { retry_after_seconds: unknown }).retry_after_seconds;
      return typeof v === 'number' ? v : undefined;
    }
    return undefined;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'request_failed';
    throw new ApiError(res.status, code, body);
  }
  return body as T;
}

/** M5.5: a session as You → Security lists it. */
export interface SessionView {
  session_id: string;
  created_at: string;
  last_seen_at: string;
  device: string;
  current: boolean;
}

export interface Me {
  person_id: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  admission: 'admitted' | 'waitlisted';
  preferred_language: string;
  /** M5.2: the zone every displayed time uses. */
  timezone?: string;
  onboarded: boolean;
  /** Connected accounts (status connected). Drives the chips and the capability list. */
  connections: number;
  /** M5 account settings (You → Account). */
  settings?: { away_email: boolean; brief_enabled: boolean; brief_time: string };
}

export interface OtpRequestResult {
  ok: true;
  kind: 'phone' | 'email';
  identifier: string;
  delivery: 'sms' | 'email' | 'dev';
  resend_cooldown_seconds: number;
  debug_code?: string;
}

export interface LocaleHint {
  country: string;
  source: 'cf' | 'default';
  resend_cooldown_seconds: number;
}

// --- product layer DTOs (mirror apps/api/src/lib/db.ts) ---------------------

export type TaskState =
  | 'understood'
  | 'planned'
  | 'awaiting_approval'
  | 'executing'
  | 'waiting'
  | 'verifying'
  | 'done'
  /** M5.1: a prepare-only outcome; nothing was created or sent. */
  | 'prepared'
  | 'failed'
  | 'unknown'
  | 'compensating'
  | 'cancelled';

export interface TaskStep {
  step_id: string;
  task_id: string;
  step_index: number;
  title: string;
  /** M5.3: the reason under a failed step. */
  description?: string | null;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  started_at: string | null;
  finished_at: string | null;
}

export interface TaskAction {
  action_id: string;
  task_id: string;
  step_id: string | null;
  tool: string;
  action_class: string;
  status: string;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export type Provider = 'google' | 'microsoft';
export type ConnectionStatus = 'connected' | 'needs_reconnect' | 'disconnected';

/** A connected account (M4). Never carries a token. */
export interface ConnectionView {
  connection_id: string;
  provider: Provider;
  account_email: string;
  status: ConnectionStatus;
  scopes: string[];
  connected_at: string;
  permissions: Permission[];
  /** M4.5: false when the account has no mailbox. */
  mail_available?: boolean;
  /** M5.3: the grant lacks a permission TWO needs; a reconnect fixes it. */
  needs_reconsent?: 'mail_draft_scope';
}

export interface PermissionView {
  connection_id: string;
  provider: Provider;
  account_email: string;
  status: ConnectionStatus;
  permissions: Permission[];
  mail_available?: boolean;
}

export interface PermissionsResponse {
  connections: PermissionView[];
  offered: Record<ActionKind, PermissionLevel[]>;
}

export interface TaskArtifact {
  kind: 'draft' | 'event' | 'message';
  id: string;
  label?: string;
  web_link?: string;
}

export interface TaskSummary {
  task_id: string;
  person_id: string;
  intent: string;
  summary: string | null;
  state: TaskState;
  risk_tier: string;
  error: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  steps: TaskStep[];
  cost_usd: string | null;
  /** M4: the connection used, the authority applied, the artifacts, the pending approval. */
  connection?: { connection_id: string; provider: Provider; account_email: string } | null;
  /** M5.2: the real title (label key plus the person's content). */
  title?: TaskTitle;
  authority?: string | null;
  /** M5.2: the authority as a key, rendered in the UI language. */
  authority_key?: string | null;
  level?: PermissionLevel | null;
  artifacts?: TaskArtifact[];
  approval?: ApprovalFrame | null;
  checklist?: string[];
  /** The one action that fixes a failed task, when known. */
  fix?: { action: 'reconnect' | 'connect' | 'permissions'; provider: Provider } | null;
  /** M5: the reminder, monitor or brief behind this task. */
  schedule?: ScheduleView | null;
}

/** A schedule as the Tasks screen shows it: next time, watch kind, status. */
export interface ScheduleView {
  schedule_id: string;
  kind: 'reminder' | 'monitor' | 'brief';
  watch: 'mail' | 'reply' | 'mail_reply' | null;
  status: 'active' | 'paused' | 'done' | 'failed';
  next_run_at: string | null;
  last_run_at: string | null;
  at: string | null;
  expires_at: string | null;
  time: string | null;
}
export type TaskView = TaskSummary;

export interface TaskDetail extends TaskSummary {
  actions: TaskAction[];
}

export type ActivityCode =
  | 'task.completed'
  | 'task.prepared'
  | 'action.executed'
  | 'action.failed'
  | 'reminder.delivered'
  | 'monitor.matched'
  | 'brief.delivered';

export interface TimelineItem {
  id: string;
  kind: 'action' | 'audit';
  /** Allowlisted activity code. */
  code: ActivityCode;
  detail?: string;
  /** M5.2: the real title; the UI renders the label in its language and isolates the subject. */
  title: TaskTitle;
  /** M5.3: the provider failure behind a failed action; the UI composes the reason line. */
  failure?: { provider: string; status?: number; reason?: string };
  status: string;
  at: string;
  task_id?: string;
}

export interface HomeSummaryItem {
  id: string;
  kind: 'task' | 'audit';
  title: string;
  code?: ActivityCode;
  at: string;
  state?: string;
  /** The honest failure line for a task that needs attention. */
  error?: string;
}

export interface HomeSummary {
  since: string;
  handled: { count: number; items: HomeSummaryItem[] };
  waiting: { count: number; items: HomeSummaryItem[] };
  /** Failed tasks since `since`: they need the person and never count as handled. */
  attention: { count: number; items: HomeSummaryItem[] };
  /** Proactive messages that waited in the conversation since `since` (the header dot). */
  unseen: number;
}

export interface MemoryItem {
  memory_id: string;
  person_id: string;
  type: 'preference' | 'fact' | 'person' | 'episodic' | 'routine';
  key: string | null;
  content: string;
  source_event_id: string | null;
  source_kind: 'stated' | 'corrected';
  confidence: number;
  status: 'active' | 'superseded' | 'forgotten';
  supersedes_id: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

export interface HistoryPage {
  conversation_id: string;
  thread_id: string;
  events: Event[];
  next_before: string | null;
  /** M4.6: current state of the approval cards carried by this page's bubbles. */
  approvals?: Record<string, { status: ApprovalCardState; checklist: string[] }>;
}

export type ApprovalCardState = 'pending' | 'approved' | 'cancelled' | 'expired' | 'failed';

export interface UploadOptions {
  duration_ms?: number | undefined;
  filename?: string | undefined;
  onProgress?: ((fraction: number) => void) | undefined;
}

/** Multipart upload through the Worker with progress (XHR: fetch has no upload progress). */
export function upload(file: Blob, opts: UploadOptions = {}): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.set('file', file, opts.filename ?? (file instanceof File ? file.name : 'upload'));
    if (opts.duration_ms !== undefined)
      form.set('duration_ms', String(Math.round(opts.duration_ms)));
    if (opts.filename) form.set('filename', opts.filename);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', apiUrl('/media/upload'));
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', null));
    xhr.onload = () => {
      const body = xhr.response as { attachment?: Attachment; error?: string } | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.attachment) resolve(body.attachment);
      else reject(new ApiError(xhr.status, body?.error ?? 'upload_failed', body));
    };
    xhr.send(form);
  });
}

export interface HealthInfo {
  ok: boolean;
  environment: string;
  build: string;
  deployed_at: string | null;
  time: string;
}

export const api = {
  /** M5.2: the API build id and deploy time (You → Account footer). */
  health: () => request<HealthInfo>('/health'),
  locale: () => request<LocaleHint>('/auth/locale'),
  requestOtp: (identifier: string, country?: string, language?: string) =>
    request<OtpRequestResult>('/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({
        identifier,
        ...(country ? { country } : {}),
        ...(language ? { language } : {}),
      }),
    }),
  otpStatus: (identifier: string, country?: string) =>
    request<{
      provider: string;
      status: 'queued' | 'sent' | 'delivered' | 'failed' | 'unknown';
      error_code?: string;
      fallback?: 'email';
    }>(
      `/auth/otp/status?identifier=${encodeURIComponent(identifier)}${country ? `&country=${encodeURIComponent(country)}` : ''}`,
    ),
  verifyOtp: (
    identifier: string,
    code: string,
    opts: { country?: string | undefined; invite_code?: string | undefined } = {},
  ) =>
    request<{ ok: true; created: boolean; me: Me }>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({
        identifier,
        code,
        ...(opts.country ? { country: opts.country } : {}),
        ...(opts.invite_code ? { invite_code: opts.invite_code } : {}),
      }),
    }),
  signOut: () => request<{ ok: true }>('/auth/signout', { method: 'POST' }),
  /** M5.5: You → Security and You → Privacy. */
  sessions: () => request<{ sessions: SessionView[] }>('/auth/sessions'),
  revokeSessions: (includeCurrent: boolean) =>
    request<{ ok: true; revoked: number; include_current: boolean }>('/auth/sessions/revoke-all', {
      method: 'POST',
      body: JSON.stringify({ include_current: includeCurrent }),
    }),
  exportData: () => request<Record<string, unknown>>('/me/export'),
  deleteMe: () =>
    request<{ ok: true; deleted: Record<string, number> }>('/me', { method: 'DELETE' }),
  me: () => request<{ me: Me }>('/me'),
  updateMe: (input: { first_name: string; last_name: string; email?: string }) =>
    request<{ me: Me }>('/me', { method: 'PATCH', body: JSON.stringify(input) }),
  /** M5: "Reach me by email when I am away" and the daily brief with its time. */
  updateSettings: (input: { away_email?: boolean; brief_enabled?: boolean; brief_time?: string }) =>
    request<{ me: Me }>('/me/settings', { method: 'PATCH', body: JSON.stringify(input) }),
  /** M5: end a reminder, a monitor or the brief. Answers "Cancelled. Nothing was changed." */
  cancelSchedule: (scheduleId: string) =>
    request<{ ok: true; line: string; schedule_id: string }>(
      `/schedules/${encodeURIComponent(scheduleId)}/cancel`,
      { method: 'POST' },
    ),
  history: (params: { before?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.before) q.set('before', params.before);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request<HistoryPage>(`/chat/history${qs ? `?${qs}` : ''}`);
  },
  homeSummary: (since?: string) =>
    request<HomeSummary>(`/home/summary${since ? `?since=${encodeURIComponent(since)}` : ''}`),
  tasks: () => request<{ tasks: TaskSummary[] }>('/tasks'),
  task: (id: string) => request<{ task: TaskDetail }>(`/tasks/${encodeURIComponent(id)}`),
  timeline: (day?: string) =>
    request<{ day: string; items: TimelineItem[] }>(
      `/tasks/timeline${day ? `?day=${encodeURIComponent(day)}` : ''}`,
    ),
  upload,
  memories: () => request<{ memories: MemoryItem[]; count: number }>('/memories'),
  updateMemory: (id: string, content: string) =>
    request<{ memory: MemoryItem; previous_id: string | null }>(
      `/memories/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ content }),
      },
    ),
  forgetMemory: (id: string) =>
    request<{ ok: true }>(`/memories/${encodeURIComponent(id)}/forget`, { method: 'POST' }),
  restoreMemory: (id: string) =>
    request<{ ok: true; memory: MemoryItem }>(`/memories/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
    }),
  // --- connections and permissions (M4) ---
  connections: () => request<{ connections: ConnectionView[] }>('/connections'),
  startConnection: (provider: Provider, returnTo: 'connections' | 'two' = 'connections') =>
    request<{ url: string }>(
      `/connections/${provider}/start${returnTo === 'two' ? '?return=two' : ''}`,
      { method: 'POST' },
    ),
  disconnectConnection: (id: string) =>
    request<{ ok: true }>(`/connections/${encodeURIComponent(id)}/disconnect`, {
      method: 'POST',
    }),
  permissions: () => request<PermissionsResponse>('/permissions'),
  updatePermission: (input: {
    connection_id: string;
    action: ActionKind;
    level: PermissionLevel;
    limit?: PermissionLimit | undefined;
  }) =>
    request<{ permission: Permission }>('/permissions', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  approveTask: (taskId: string, input: { approval_id: string; signature: string }) =>
    request<{ ok: true }>(`/tasks/${encodeURIComponent(taskId)}/approve`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  cancelTask: (taskId: string, input: { approval_id: string }) =>
    request<{ ok: true }>(`/tasks/${encodeURIComponent(taskId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// --- history prefetch: started with the session probe and right after verify -------------

let prefetched: Promise<HistoryPage> | null = null;

/** Start loading the first history page now; the agent store picks it up when it mounts. */
export function prefetchHistory(limit = 50): void {
  prefetched = api.history({ limit }).catch((err: unknown) => {
    prefetched = null;
    throw err;
  });
  prefetched.catch(() => undefined);
}

export function takePrefetchedHistory(): Promise<HistoryPage> | null {
  const p = prefetched;
  prefetched = null;
  return p;
}

// Pending invite code from /invite/<code>; not identity, just a hint carried to sign-in.
const INVITE_KEY = 'pa.invite_code';
export function rememberInviteCode(code: string): void {
  try {
    sessionStorage.setItem(INVITE_KEY, code.toUpperCase());
  } catch {
    // storage unavailable: the code is simply not carried over
  }
}
export function takeInviteCode(): string | undefined {
  try {
    return sessionStorage.getItem(INVITE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
export function clearInviteCode(): void {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    // ignore
  }
}

// Last visit marker for "While you were away" (per browser; the server falls back to the session start).
const LAST_SEEN_KEY = 'pa.last_seen';
export function readLastSeen(): string | undefined {
  try {
    return localStorage.getItem(LAST_SEEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
export function writeLastSeen(iso = new Date().toISOString()): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, iso);
  } catch {
    // ignore
  }
}

// Remembered sign-in country (per browser).
const COUNTRY_KEY = 'pa.country';
export function readRememberedCountry(): string | undefined {
  try {
    return localStorage.getItem(COUNTRY_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
export function rememberCountry(code: string): void {
  try {
    localStorage.setItem(COUNTRY_KEY, code);
  } catch {
    // ignore
  }
}
